import { useState } from 'react';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { delay, STAGGER } from '@caios/ui';
import { authClient } from '../auth.ts';
import { afterSignIn } from './signin.tsx';
import { TotpEnrol } from '../components/totp-enrol.tsx';

/**
 * Staff and partners must add a second factor before anything else (ADR-004). Also the landing page of
 * an owner break-glass link: a recovery session can do nothing here except add a new factor.
 */
export function SecuritySetup() {
  const navigate = useNavigate();
  const { recovery } = useRouteContext({ from: '/security/setup' }) as { recovery: boolean };
  const [mode, setMode] = useState<'choose' | 'totp'>('choose');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function addPasskey() {
    setBusy(true); setError(null);
    const r = await authClient.passkey.addPasskey({ name: navigator.platform ? `${navigator.platform} passkey` : 'Passkey' });
    setBusy(false);
    if (r?.error) return setError('The passkey wasn’t saved. Try again, or use an authenticator app.');
    return afterSignIn(navigate);
  }

  return (
    <div className="cx-app auth-screen">
      <section className="cx-hero-panel auth-card cx-rise">
        <div className="cx-eyebrow">CAIOS · <b>{recovery ? 'account recovery' : 'secure your account'}</b></div>
        {mode === 'choose' && (<>
          <h1 className="cx-hero cx-rise" style={delay(1, STAGGER.list)}>{recovery ? 'Add a new way to sign in' : 'Add a second way to prove it’s you'}</h1>
          <p className="cx-body cx-rise" style={delay(2, STAGGER.list)}>{recovery
            ? 'You’re signed in with a one-time recovery link. Register a new passkey or authenticator to get back in, then remove the lost one on the Security page.'
            : 'Team and partner accounts need this before they can open CAIOS. It takes under a minute.'}</p>
          <button type="button" className="cx-btn cx-btn--primary auth-wide cx-rise" style={delay(3, STAGGER.list)} onClick={addPasskey} disabled={busy} data-testid="setup-passkey">
            {busy ? 'Waiting for your device…' : 'Use a passkey (recommended)'}
          </button>
          <p className="cx-label cx-rise" style={delay(4, STAGGER.list)}>Face ID, Touch ID, Windows Hello or your phone. Nothing to type next time.</p>
          <button type="button" className="cx-btn auth-wide cx-rise" style={delay(5, STAGGER.list)} onClick={() => setMode('totp')} disabled={busy} data-testid="setup-totp">
            Use an authenticator app instead
          </button>
        </>)}
        {mode === 'totp' && <TotpEnrol onDone={() => afterSignIn(navigate)} onCancel={() => setMode('choose')} />}
        {error && <p className="auth-error" role="alert">{error}</p>}
      </section>
    </div>
  );
}
